import { AnyAction } from 'redux';

import { signOutSuccess } from 'modules/auth/actions/auth.actions';
import * as ActionTypes from 'modules/forms/actions/form.actions';
import {
  FORM_UUID_CONTRACEPTIVE_HEALTH,
  FORM_UUID_GENERAL_PRESCREENING,
  FORM_UUID_TRAVEL_CLINIC,
  FORM_UUID_WEIGHT_LOSS_CLINIC,
} from 'constants/app.constants';
import { EUnitSystem } from 'interfaces/patients.interfaces';
import { IAppointment, IForm, IPharmacyService } from '../interfaces/form.interfaces';
import { contraceptiveHealthForm, travelClinicForm } from '../constants/forms.constants';
import {
  contraceptiveHealthRoutes,
  generalPrescreeningRoutes,
  travelClinicRoutes,
  weightLossClinicRoutes,
} from '../constants/routes.constants';
import { weightLossClinicForm } from '../pages/WeightLossClinic/form.constants';
import { shouldDisplaySection } from '../helpers/sectionConditions.helper';
import { convertInchesToFeetAndInches } from '../helpers/healthInfo.helper';
import { generalPrescreeningForm } from '../pages/GeneralPrescreening/form.constants';

export interface IFormStore {
  appointment: IAppointment | null;
  selectedService: IPharmacyService | null;
  formUuid: string;
  formTitle: string;
  currentForm: IForm | null;
  currentRoutes: string[];
  currentStep: number;
  form: IForm;
  loading: {
    getForm: boolean;
    postForm: boolean;
  };
  errors: {
    getForm: string | null;
  };
  modals: {
    termsOfService: boolean;
    privacyPolicy: boolean;
  };
}

const initialState: IFormStore = {
  appointment: null,
  selectedService: null,
  formUuid: '',
  formTitle: '',
  currentForm: null,
  currentRoutes: [],
  currentStep: 0,
  form: {
    status: undefined,
    passesGeneralQualification: undefined,
    hasSymptoms: undefined,
    symptoms: [],
    photos: [],
    episodes: undefined,
    statements: [],
    additionalInformation: '',
    additionalSymptoms: [],
    secondarySymptoms: [],
    hasSecondarySymptoms: undefined,
    hasThirdSymptoms: undefined,
    hasFourthSymptoms: undefined,
    disqualificationType: undefined,
    destination: '',
    tripStart: '',
    tripLength: '',
    travelType: [],
    travellingWith: '',
    accomodation: [],
    accomodationOther: '',
  },
  loading: {
    getForm: false,
    postForm: false,
  },
  errors: {
    getForm: null,
  },
  modals: {
    termsOfService: false,
    privacyPolicy: false,
  },
};

const formReducer = (state = initialState, action: AnyAction): IFormStore => {
  switch (action.type) {
    case ActionTypes.setFormUuid.type: {
      const { uuid, title, customer: actionCustomer, guest } = action.payload;

      const customer = actionCustomer;
      const customerHealthInfo = customer?.healthInfo;
      const healthMeasurementValue = customerHealthInfo
        ? {
            ...(customerHealthInfo || {}),
            ...convertInchesToFeetAndInches(customerHealthInfo.height),
          }
        : {
            unitSystem: EUnitSystem.METRIC,
          };

      const form = (() => {
        switch (uuid) {
          case FORM_UUID_CONTRACEPTIVE_HEALTH:
            return contraceptiveHealthForm?.sections
              ? {
                  ...contraceptiveHealthForm,
                  sections: contraceptiveHealthForm.sections
                    .filter(section => shouldDisplaySection(section.conditions, customer, guest))
                    .map(section => ({
                      ...section,
                      questions: section.questions.map(question =>
                        question.type === 'HEALTH_MEASUREMENT'
                          ? { ...question, value: healthMeasurementValue }
                          : question,
                      ),
                    })),
                }
              : contraceptiveHealthForm;
          case FORM_UUID_TRAVEL_CLINIC:
            return travelClinicForm?.sections
              ? {
                  ...travelClinicForm,
                  sections: travelClinicForm.sections
                    .filter(section => shouldDisplaySection(section.conditions, customer, guest))
                    .map(section => ({
                      ...section,
                      questions: section.questions.map(question =>
                        question.type === 'HEALTH_MEASUREMENT'
                          ? { ...question, value: healthMeasurementValue }
                          : question,
                      ),
                    })),
                }
              : travelClinicForm;
          case FORM_UUID_WEIGHT_LOSS_CLINIC:
            return weightLossClinicForm?.sections
              ? {
                  ...weightLossClinicForm,
                  sections: weightLossClinicForm.sections
                    .filter(section => shouldDisplaySection(section.conditions, customer, guest))
                    .map(section => ({
                      ...section,
                      questions: section.questions.map(question =>
                        question.type === 'HEALTH_MEASUREMENT'
                          ? { ...question, value: healthMeasurementValue }
                          : question,
                      ),
                    })),
                }
              : weightLossClinicForm;
          case FORM_UUID_GENERAL_PRESCREENING:
            return generalPrescreeningForm?.sections
              ? {
                  ...generalPrescreeningForm,
                  sections: generalPrescreeningForm.sections
                    .filter(section => shouldDisplaySection(section.conditions, customer, guest))
                    .map(section => ({
                      ...section,
                      questions: section.questions.map(question =>
                        question.type === 'HEALTH_MEASUREMENT'
                          ? { ...question, value: healthMeasurementValue }
                          : question,
                      ),
                    })),
                }
              : generalPrescreeningForm;
          default:
            return null;
        }
      })();

      return {
        ...state,
        formUuid: uuid,
        formTitle: title,
        currentForm: form,
        currentRoutes: (() => {
          switch (uuid) {
            case FORM_UUID_CONTRACEPTIVE_HEALTH:
              return contraceptiveHealthRoutes;
            case FORM_UUID_TRAVEL_CLINIC:
              return travelClinicRoutes;
            case FORM_UUID_WEIGHT_LOSS_CLINIC:
              return weightLossClinicRoutes;
            case FORM_UUID_GENERAL_PRESCREENING:
              return generalPrescreeningRoutes;
            default:
              return [];
          }
        })(),
        currentStep: 0,
      };
    }
    case ActionTypes.setSelectedService.type:
      return {
        ...state,
        selectedService: action.payload,
      };
    case ActionTypes.setCurrentFormStep.type:
      return {
        ...state,
        currentStep: action.payload as number,
      };
    case ActionTypes.getFormAppointment.type:
      return {
        ...state,
        loading: {
          ...state.loading,
          getForm: true,
        },
      };
    case ActionTypes.getFormAppointmentSuccess.type: {
      const appointment = action.payload as IAppointment;
      return {
        ...state,
        appointment,
        selectedService: appointment.service,
        loading: {
          ...state.loading,
          getForm: false,
        },
      };
    }
    case ActionTypes.getFormAppointmentError.type:
      return {
        ...state,
        loading: {
          ...state.loading,
          getForm: false,
        },
      };
    case ActionTypes.postAppointmentForm.type:
      return {
        ...state,
        loading: {
          ...state.loading,
          postForm: true,
        },
      };
    case ActionTypes.postAppointmentFormSuccess.type:
      return {
        ...state,
        form: {
          ...state.form,
          uuid: action.payload.uuid,
          disqualificationType: action.payload.status,
        },
        loading: {
          ...state.loading,
          postForm: false,
        },
      };
    case ActionTypes.postAppointmentFormError.type:
      return {
        ...state,
        loading: {
          ...state.loading,
          postForm: false,
        },
      };
    case ActionTypes.setFormData.type:
      return {
        ...state,
        form: {
          ...state.form,
          ...action.payload,
        },
      };
    case ActionTypes.setCurrentFormData.type:
      return {
        ...state,
        currentForm: {
          ...state.form,
          ...action.payload,
        },
      };
    case ActionTypes.togglePrivacyPolicyModal.type:
      return {
        ...state,
        modals: {
          ...state.modals,
          privacyPolicy: action.payload as boolean,
        },
      };
    case ActionTypes.toggleTermsOfServiceModal.type:
      return {
        ...state,
        modals: {
          ...state.modals,
          termsOfService: action.payload as boolean,
        },
      };
    case ActionTypes.clearFormData.type:
      return {
        ...state,
        form: { ...initialState.form },
      };
    case ActionTypes.clearForm.type:
    case signOutSuccess.type:
      return initialState;
    default:
      return state;
  }
};

export default formReducer;
